import React from "react";
import { TransformedPairOrDuration } from "./types";
import { ShiftThresholds, TimesheetsShiftInfo } from "../../../types/Shift.types";
import { AuditHistoryItem } from "./AuditHistoryItem";
import { AuditTimestampPairDisplay } from "./AuditTimestampPairDisplay";
import { getEditLabel, getEditDate, getEditAuthor } from "./utils";
import { isTransformedDuration } from "./utils/auditDisplayUtils";
import { AuditTimestampDurationDisplay } from "./AuditTimestampDurationDisplay";

export type UseAuditHistoryItemsProps = {
  historicalEdits: TransformedPairOrDuration[];
  timezone: string;
  shiftThresholds: ShiftThresholds;
  keyPrefix: string;
  shiftInfo: TimesheetsShiftInfo;
};

/**
 * Custom hook that transforms historical edits into AuditHistoryItem components
 * @param historicalEdits - Array of historical edits to display
 * @param timezone - Timezone for timestamp display
 * @param shiftThresholds - Thresholds for shift validation
 * @param keyPrefix - Prefix for generating unique keys (e.g., "clockinout" or "break1")
 * @param shiftInfo - Shift information
 * @returns Array of AuditHistoryItem JSX elements
 */
export const useAuditHistoryItems = ({
  historicalEdits,
  timezone,
  shiftThresholds,
  keyPrefix,
  shiftInfo,
}: UseAuditHistoryItemsProps): JSX.Element[] => {
  return historicalEdits.map((edit, index) => {
    const editKey = `${keyPrefix}-history-${index}`;

    const content = isTransformedDuration(edit) ? (
      <AuditTimestampDurationDisplay
        edit={edit}
        shiftInfo={shiftInfo}
        shiftKey={editKey}
      />
    ) : (
      <AuditTimestampPairDisplay
        edit={edit}
        timezone={timezone}
        shiftThresholds={shiftThresholds}
        shiftKey={editKey}
        isHistoricalDeleted={edit.deleted}
        shiftInfo={shiftInfo}
      />
    );

    return (
      <AuditHistoryItem
        key={editKey}
        label={getEditLabel(edit)}
        date={getEditDate(edit)}
        author={getEditAuthor(edit)}
        deleted={edit.deleted}
        content={content}
      />
    );
  });
};
